import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { wrapFunctionalComponent } from 'utils/framework';
import { colors, mediaQueries } from 'style/config';
import {
    Grid, Button, Box, Text, Image
} from 'components/ui';
import { isAnonymousSelector } from 'selectors/auth/isAnonymousSelector';
import { userSelector } from 'selectors/user/userSelector';
import store from 'store/Store';
import actions from 'Actions';
import anaUtils from 'analytics/utils';
import Location from 'utils/Location';
import authUtils from 'utils/Authentication';
import Avatar from 'components/Avatar';
import helpersUtils from 'utils/Helpers';
import { showBiSignInUpAccountCopyUpdateSelector } from 'viewModel/selectors/testTarget/showBiSignInUpAccountCopyUpdateSelector';
import { isCreativeRefreshPhase2Enabled as getIsCreativeRefreshPhase2Enabled } from 'utils/BeautyInsider';
import userUtils from 'utils/User';

import localeUtils from 'utils/LanguageLocale';
import GreetingText from 'components/GreetingText';
import Markdown from 'components/Markdown/Markdown';
import { HEADER_VALUE } from 'constants/authentication';
import AccountGreetingEN from 'components/Header/AccountGreeting/locales/AccountGreeting_en_US';
import AccountGreetingFR from 'components/Header/AccountGreeting/locales/AccountGreeting_fr_CA';

const { getLocalisedDataFromResource } = localeUtils;

const getText = getLocalisedDataFromResource(AccountGreetingEN, AccountGreetingFR);
const { deferTaskExecution } = helpersUtils;

const isAccountGreetingAnonymousSelector = createSelector(
    isAnonymousSelector,
    userSelector,
    (isAnonymous, user) => isAnonymous && user?.profileStatus !== userUtils.PROFILE_STATUS.LOGGED_IN
);

const signIn = onDismiss => {
    deferTaskExecution(() => {
        const navInfo = anaUtils.buildNavPath(['top nav', 'account', 'sign-in']);

        store.dispatch(
            actions.showSignInModal({
                isOpen: true,
                source: authUtils.SIGN_IN_SOURCES.ACCOUNT_GREETING,
                analyticsData: { navigationInfo: navInfo },
                callback: onDismiss ? onDismiss : null,
                extraParams: { headerValue: HEADER_VALUE.USER_CLICK }
            })
        );
    });
};

const register = onDismiss => {
    deferTaskExecution(() => {
        const navInfo = anaUtils.buildNavPath(['top nav', 'account', 'register']);
        const openPostBiSignUpModal = !Location.isCheckout() && !Location.isMySephoraPage();
        store.dispatch(
            actions.showRegisterModal({
                isOpen: true,
                openPostBiSignUpModal,
                analyticsData: { navigationInfo: navInfo },
                callback: onDismiss ? onDismiss : null
            })
        );
    });
};

const showBiCardModal = (profileId, onDismiss) => {
    store.dispatch(actions.showBiCardModal({ isOpen: true, profileId }));
    onDismiss?.();
};

function AccountGreeting({ onDismiss }) {
    const isAnonymous = useSelector(isAccountGreetingAnonymousSelector);
    const user = useSelector(userSelector);
    const showBiSignInUpAccountCopyUpdate = useSelector(showBiSignInUpAccountCopyUpdateSelector);
    const isCreativeRefreshPhase2Enabled = useMemo(() => getIsCreativeRefreshPhase2Enabled(), []);

    const showCopyUpdate = isAnonymous && (showBiSignInUpAccountCopyUpdate.challengerOne || showBiSignInUpAccountCopyUpdate.challengerTwo);
    const showScanInStore = isCreativeRefreshPhase2Enabled && !isAnonymous && user?.beautyInsiderAccount && user?.profileId;

    const beautyInsiderStyles = {
        width: 172,
        height: 24,
        marginBottom: 2,
        marginLeft: -1
    };

    return (
        <div>
            <Grid
                columns={showScanInStore ? 'auto minmax(0, 1fr) auto' : 'auto 1fr'}
                alignItems='center'
                gap={3}
                lineHeight='tight'
            >
                {!showCopyUpdate ? (
                    <>
                        <Avatar size={52} />
                        <div>
                            <GreetingText data-at={Sephora.debug.dataAt('person_greeting')} />
                            {isAnonymous && (
                                <Markdown
                                    fontSize='sm'
                                    marginTop={1}
                                    maxWidth={[null, null, '18em']} // avoid widow
                                    content={getText('lead')}
                                />
                            )}
                        </div>
                        {showScanInStore && (
                            <button
                                type='button'
                                css={styles.scanInStoreButton}
                                data-at={Sephora.debug.dataAt('show_card_link')}
                                onClick={() => showBiCardModal(user.profileId, onDismiss)}
                            >
                                <Image
                                    alt=''
                                    aria-hidden='true'
                                    disableLazyLoad={true}
                                    src='/img/ufe/icons/10_sephora_cc.svg'
                                    width={27}
                                    height={20}
                                />
                                <span css={styles.scanInStoreText}>{getText('scanInStore')}</span>
                            </button>
                        )}
                    </>
                ) : (
                    <Box>
                        <Image
                            alt={getText('biLogoAltText')}
                            disableLazyLoad={true}
                            display='block'
                            src='/img/ufe/bi/logo-beauty-insider.svg'
                            {...beautyInsiderStyles}
                        />
                        <Box>
                            <Text
                                is='h2'
                                lineHeight='tight'
                                fontWeight='bold'
                                children={getText('joinOurLoyaltyProgram')}
                            />
                            <Text
                                is='p'
                                lineHeight='tight'
                                children={getText('getFreeShipping')}
                            />
                            <Text
                                is='p'
                                lineHeight='tight'
                                children={getText('getFreeBirthdayGift')}
                            />
                            <Text
                                is='p'
                                lineHeight='tight'
                                children={getText('earnOnePointPerSpent')}
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
            {isAnonymous && (
                <Grid
                    marginTop={3}
                    gap={3}
                    columns={2}
                >
                    <Button
                        onClick={() => signIn(onDismiss)}
                        data-at={Sephora.debug.dataAt('sign_in_menu')}
                        block={true}
                        variant='primary'
                        size={showBiSignInUpAccountCopyUpdate.challengerOne ? 'lg' : 'sm'}
                        children={getText('signIn')}
                    />
                    <Button
                        onClick={() => register(onDismiss)}
                        data-at={Sephora.debug.dataAt('create_account_menu')}
                        block={true}
                        variant='secondary'
                        size={showBiSignInUpAccountCopyUpdate.challengerOne ? 'lg' : 'sm'}
                        children={getText('createAccount')}
                    />
                </Grid>
            )}
        </div>
    );
}

const styles = {
    scanInStoreButton: {
        appearance: 'none',
        background: 'transparent',
        border: 0,
        color: colors.gray,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        lineHeight: 1,
        padding: 0,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        [mediaQueries.sm]: {
            display: 'none'
        }
    },
    scanInStoreText: {
        marginTop: 4,
        fontSize: 10,
        lineHeight: '11px'
    }
};

export default wrapFunctionalComponent(AccountGreeting, 'AccountGreeting');
